import { Dialog, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { isLengthEq, isLengthGt, isPresent, isSame } from 'lib/utils'

import Image from 'next/image'
import { Fragment, useMemo, useState } from 'react'
import {
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import { ImageModalProps } from './ImageModalDsk'
import NextButton from './NextButton'
import PrevButton from './PrevButton'

let ImageModalMbl = ({
  src,
  images,
  idx,
  isOpen,
  setIsOpen,
}: ImageModalProps) => {
  let [imageIdx, setImageIdx] = useState(idx)

  let isSlider = useMemo(
    () => isPresent(images) && isPresent(idx),
    [idx, images]
  )

  useIsomorphicLayoutEffect(() => {
    if (!isSlider) {
      return
    }

    isOpen && setImageIdx(idx)
    let timeout: NodeJS.Timeout
    if (!isOpen) {
      timeout = setTimeout(() => setImageIdx(idx), 300)
    }

    return () => {
      isPresent(timeout) && clearTimeout(timeout)
    }
  }, [idx, isOpen, isSlider])

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as='div'
        className='overflow-hidden fixed inset-0 bg-white z-60'
        onClose={() => setIsOpen(false)}
      >
        <TransformWrapper reset={{ animation: false }}>
          {({
            setTransform,
          }: {
            setTransform: (
              positionX: number,
              positionY: number,
              scale: number,
              animationTime: number,
              animationType: string
            ) => void
          }) => (
            <Transition.Child
              as='div'
              enter='ease-in duration-75 bg-white'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-75'
              leaveFrom='opacity-100'
              leaveTo='opacity-0 bg-white'
            >
              {isLengthGt(1, images ?? []) && (
                <div
                  className='flex top-8 justify-center w-full absolute z-10 text-sm mx-auto bg-opacity-60 select-none'
                  // shadow='1'
                >
                  <span className='px-2 text-mediumDark font-semibold text-sm border rounded border-[rgba(224,224,224,0.7)] shadow bg-white'>
                    {(imageIdx ?? 0) + 1} / {images!.length}
                  </span>
                </div>
              )}
              <div className='relative flex items-start'>
                <button
                  aria-label='Закрыть'
                  type='button'
                  className='absolute top-8 right-3 md:right-5 focus:outline-none active:bg-lightGrey z-10 bg-white'
                  onClick={() => {
                    setTransform(0, 0, 1, 0, 'linear')
                    setIsOpen(false)
                  }}
                >
                  <XIcon
                    className='h-6 w-6 -m-1'
                    aria-hidden='true'
                  />
                </button>
              </div>
              <TransformComponent>
                <div className='relative w-screen h-screen'>
                  <Image
                    src={isSlider ? images![imageIdx!]! : src}
                    alt=''
                    layout='fill'
                    objectFit='contain'
                  />
                </div>
              </TransformComponent>

              <svg
                className='absolute left-2 top-6 z-10 p-1 w-12 h-12 bg-white bg-opacity-60 rounded pointer-events-none select-none'
                xmlns='http://www.w3.org/2000/svg'
                width='1000'
                height='1000'
                x='0'
                y='0'
                fill='currentColor'
                version='1.1'
                viewBox='0 0 1000 1000'
                xmlSpace='preserve'
              >
                <path d='M57.02 161.69 10 208.7l11.24 11.24c6.13 6.13 12.06 11.24 13.29 11.24 1.02 0 13.29-11.24 26.98-24.94l25.14-25.14v176.82h34.76v-87.9c0-48.45.61-87.9 1.43-87.9s12.88 11.24 26.57 24.94l25.14 25.14 12.27-12.26 12.27-12.27-46.4-46.4c-25.55-25.76-47.02-46.61-47.63-46.61-.62.01-22.29 21.06-48.04 47.03zm241.62 51.1c-17.78 1.84-33.12 4.29-33.93 5.32-2.25 2.66-31.48 68.89-31.48 71.34 0 1.23 47.83 68.28 106.5 148.82 58.46 80.54 106.09 146.56 105.48 146.56-.61 0-21.67-9.2-46.81-20.44-25.35-11.24-46.81-20.44-48.04-20.44-1.63 0-56.42 38.64-63.98 44.97-1.84 1.63 6.13 79.93 8.38 81.97.82.62 30.05 13.9 64.8 29.23l63.37 28.21 28.62 26.37c15.74 14.52 32.5 29.64 37 33.73l8.58 7.15 82.18 13.08c67.46 10.63 82.59 13.7 84.83 16.76 1.63 2.04 11.86 16.35 22.9 31.69 11.04 15.33 20.65 28.01 21.26 28.21.82 0 7.36-4.5 14.72-9.81l13.49-9.81-26.57-36.8c-14.72-20.03-27.39-37.2-28.21-38.02-.61-.82-38.84-7.56-84.83-14.92-45.79-7.36-84.43-13.9-85.65-14.72-1.43-.82-17.58-15.33-36.18-32.3l-33.73-30.87-57.65-25.55c-64.6-28.42-58.67-23.51-61.12-50.9l-1.24-15.32 16.15-11.65 16.35-11.86 79.52 35.57c43.75 19.42 88.11 39.04 98.53 43.74 10.43 4.7 18.19 7.36 17.58 5.93-.82-1.43-63.58-87.7-139.21-191.95-75.64-104.25-137.57-189.91-137.57-190.72 0-3.68 15.33-35.77 17.38-36.59 1.43-.41 10.63-1.43 20.65-2.25l18.19-1.43 84.63 116.72 84.63 116.52 8.38-5.93c4.5-3.48 10.83-8.18 13.9-10.83l5.52-4.5-20.44-28.21-20.65-28.01 7.97-18.4c8.79-20.24 6.34-18.81 35.36-21.46l13.29-1.23 20.03 27.6c11.04 15.33 20.85 27.8 21.87 27.8 2.66 0 26.37-16.97 26.37-19.01.2-.82-5.72-9.81-13.08-19.62-7.36-10.02-13.29-19.42-13.29-20.85 0-1.64 3.27-10.43 7.36-19.42 8.18-18.6 5.93-17.38 35.98-20.44l13.49-1.43 13.08 19.62c6.95 10.83 13.7 19.83 14.72 19.83 3.88 0 27.6-19.22 25.96-21.26-.82-1.23-5.52-7.56-10.22-13.9l-8.58-11.86 7.77-17.58c4.91-11.45 8.99-18.19 11.86-19.22 2.25-.82 11.45-2.04 20.44-2.86l16.56-1.43 62.14 85.45 62.14 85.45 26.78 96.08 26.57 96.28 26.37 36.18c16.15 22.28 27.19 35.57 28.62 34.75 1.84-1.02 22.69-15.94 25.55-18.19.41-.41-10.43-15.74-23.92-34.34l-24.74-33.73-26.56-96.08-26.58-95.87-68.68-94.85c-37.82-51.92-69.5-95.26-70.32-96.28-.82-.82-18.81.41-39.86 2.66s-39.04 4.09-39.86 4.09c-.82 0-4.9 8.18-9.2 18.19-5.72 13.49-8.99 18.6-12.27 19.22-2.25.61-19.83 3.68-38.63 6.75l-34.34 5.52-5.93 13.49c-11.65 26.17-6.54 23.3-47.02 27.19-19.62 1.84-37.61 3.47-40.07 3.47-3.27.2-5.93 3.88-11.86 17.99-4.29 10.02-7.97 18.19-8.38 18.6-.21.2-26.57-35.36-58.46-79.31l-57.85-79.74-7.36.2c-3.88.21-21.66 1.85-39.45 3.89zM86.66 550.49V639l-25.14-25.14c-13.7-13.7-25.96-24.94-26.98-24.94-1.23 0-7.15 5.11-13.29 11.24L10 611.41l47.63 47.63 47.43 47.43 47.02-47.02 47.02-47.02-12.27-12.26-12.27-12.27-25.14 25.14c-13.7 13.7-25.76 24.94-26.57 24.94-.82 0-1.43-39.66-1.43-87.9v-87.9H86.66v88.31z' />
              </svg>
              {/* </button> */}
            </Transition.Child>
          )}
        </TransformWrapper>

        {isSlider && (
          <>
            <div className='absolute left-4 opacity-90 top-1/2 -translate-y-1/2'>
              <PrevButton
                onClick={() =>
                  setImageIdx((prev) =>
                    isSame(prev, 0) ? images!.length - 1 : prev! - 1
                  )
                }
              />
            </div>

            <div className='absolute right-4 opacity-90 top-1/2 -translate-y-1/2'>
              <NextButton
                onClick={() =>
                  setImageIdx((prev) =>
                    isLengthEq(imageIdx! + 1, images) ? 0 : prev! + 1
                  )
                }
              />
            </div>
          </>
        )}
      </Dialog>
    </Transition.Root>
  )
}

export default ImageModalMbl
